/**
 * RegistryService - получение модулей и версий от API
 *
 * Endpoints:
 * - GET /api/org/{slug}/versions — версии для кеширования
 * - GET /api/org/{slug}/modules — список модулей организации
 * - GET /api/modules/{id}/manifest — манифест конкретного модуля
 */

import { PLATFORM_LANGUAGES } from '@/config/languages'
import type { ModuleManifest, OrgContext, OrgModulesResponse, VersionsInfo } from '@/shell/types'
import { CACHE_KEYS } from '@/shell/types'
import type { LocalizedString } from '@/types/theme'
import { getCacheService } from './CacheService'

/**
 * Тип конфига организации из Admin API
 */
interface AdminOrgConfig {
  id: string
  slug: string
  name: LocalizedString
  description?: LocalizedString
  type: string
  logo?: string
  domains: string[]
  features: string[]
  languages?: string[]
  quotas: {
    maxUsers: number
    maxProjects: number
    maxStorage: number
  }
  status: string
  stats: {
    usersCount: number
    projectsCount: number
    storageUsed: number
  }
  createdAt: string
  updatedAt: string
}

/**
 * Конфигурация RegistryService
 */
export interface RegistryConfig {
  /** Базовый URL API */
  apiBase: string
  /** Таймаут запросов в мс */
  timeout?: number
}

/**
 * Mock данные для разработки (пока нет backend)
 */
const MOCK_MODULES: ModuleManifest[] = [
  {
    id: 'consultant',
    title: 'AI Консультант',
    description: 'AI-консультант по возможностям платформы',
    icon: 'mdi-robot-happy',
    entryUrl: '/modules/consultant/entry.js',
    basePath: '/api/consultant',
    version: '1.0.0',
    requiresAuth: false,
    menu: { section: 'tools', order: 0 },
    features: ['consultant-basic', 'consultant-chat'],
  },
  {
    id: 'tagcloud',
    title: 'Облако тегов',
    description: 'Интерактивное облако тегов для визуализации идей',
    icon: 'mdi-cloud',
    entryUrl: '/modules/tagcloud/entry.js',
    basePath: '/api/tagcloud',
    version: '1.0.0',
    requiresAuth: false,
    menu: { section: 'main', order: 1 },
    features: ['tagcloud-basic', 'tagcloud-export'],
  },
  {
    id: 'presentations',
    title: 'Презентации',
    description: 'Создание и проведение интерактивных презентаций',
    icon: 'mdi-presentation',
    entryUrl: '/modules/presentations/entry.js',
    basePath: '/api/presentations',
    version: '1.0.0',
    requiresAuth: false,
    menu: { section: 'main', order: 2 },
    features: ['presentations-basic', 'presentations-polls'],
  },
  {
    id: 'lms',
    title: 'Обучение',
    description: 'Система управления обучением с курсами и тестами',
    icon: 'mdi-school',
    entryUrl: '/modules/lms/entry.js',
    basePath: '/api/lms',
    version: '1.0.0',
    requiresAuth: true,
    menu: { section: 'main', order: 3 },
    features: ['lms-courses', 'lms-tests', 'lms-certificates'],
  },
  {
    id: 'analytics',
    title: 'Аналитика',
    description: 'Дашборды и отчёты по активности',
    icon: 'mdi-chart-line',
    entryUrl: '/modules/analytics/entry.js',
    basePath: '/api/analytics',
    version: '1.0.0',
    requiresAuth: true,
    menu: { section: 'tools', order: 1 },
    features: ['analytics-basic', 'analytics-export'],
    requiredRoles: ['admin', 'teacher'],
  },
  {
    id: 'chat',
    title: 'Чат',
    description: 'Мессенджер для общения участников',
    icon: 'mdi-chat',
    entryUrl: '/modules/chat/entry.js',
    basePath: '/api/chat',
    version: '1.0.0',
    requiresAuth: true,
    menu: { section: 'main', order: 4 },
    features: ['chat-basic', 'chat-groups'],
  },
]

/**
 * Mock данные организаций
 */
const MOCK_ORGS: Record<string, Partial<OrgContext>> = {
  'www.tlh.ru': {
    id: 'org-public',
    slug: 'www.tlh.ru',
    name: 'TLH Public',
    type: 'public',
  },
  university: {
    id: 'org-university',
    slug: 'university',
    name: 'Университет',
    type: 'university',
  },
  corporate: {
    id: 'org-corporate',
    slug: 'corporate',
    name: 'Корпоративный портал',
    type: 'corporate',
  },
}

/**
 * Сервис реестра модулей
 */
// API URL from environment (API Gateway in production, /api/consultant for dev proxy)
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/consultant'

export class RegistryService {
  // TODO: Use config when real API is implemented
  private _config: RegistryConfig
  private cache = getCacheService()

  constructor(config: Partial<RegistryConfig> = {}) {
    this._config = {
      apiBase: config.apiBase ?? API_BASE_URL,
      timeout: config.timeout ?? 10000,
    }
  }

  /** Get current config (for debugging) */
  getConfig(): RegistryConfig {
    return { ...this._config }
  }

  /**
   * Получить версии для проверки актуальности кеша
   * В dev-режиме всегда возвращаем новую версию чтобы получать свежие данные
   * В prod используем хеш сборки для автоматической инвалидации при каждом деплое
   */
  async getVersions(_orgSlug: string): Promise<VersionsInfo> {
    // TODO: Реальный запрос к API
    // const response = await fetch(`${this.config.apiBase}/org/${_orgSlug}/versions`)
    // return response.json()

    // В dev-режиме используем timestamp для инвалидации кеша
    // В prod используем __BUILD_HASH__ (git commit hash) - автоматически меняется при каждой сборке
    const isDev = import.meta.env.DEV
    const manifestVersion = isDev ? `dev-${Date.now()}` : `1.0.0-${__BUILD_HASH__}`

    return {
      manifest: manifestVersion,
      modules: MOCK_MODULES.reduce(
        (acc, m) => {
          acc[m.id] = m.version
          return acc
        },
        {} as Record<string, string>
      ),
      timestamp: new Date().toISOString(),
    }
  }

  /**
   * Получить модули организации с кешированием
   */
  async getOrgModules(orgSlug: string): Promise<OrgModulesResponse> {
    const cacheKey = CACHE_KEYS.orgModules(orgSlug)
    const versions = await this.getVersions(orgSlug)

    return this.cache.getWithVersion(cacheKey, versions.manifest, async () => {
      return this.fetchOrgModules(orgSlug)
    })
  }

  /**
   * Получить модули без кеширования (force refresh)
   * Загружает конфиг организации из Admin API и фильтрует модули по features
   */
  async fetchOrgModules(orgSlug: string): Promise<OrgModulesResponse> {
    // Загружаем реальный конфиг организации из Admin API
    let orgConfig: AdminOrgConfig | null = null

    try {
      // Admin API находится внутри consultant function: /api/consultant/admin/*
      // В DEV: apiBase = /api, нужен полный путь /api/consultant/admin/*
      // В PROD: VITE_API_URL = https://.../api/consultant, нужен /admin/*
      const adminPath = this._config.apiBase.includes('/consultant')
        ? `/admin/orgs/${orgSlug}`
        : `/consultant/admin/orgs/${orgSlug}`
      const response = await fetch(`${this._config.apiBase}${adminPath}`, {
        signal: AbortSignal.timeout(this._config.timeout ?? 10000),
      })

      if (response.ok) {
        orgConfig = await response.json()
      } else if (import.meta.env.DEV) {
        console.warn(`[Registry] Failed to load org ${orgSlug}: ${response.status}`)
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`[Registry] Error loading org ${orgSlug}:`, error)
      }
    }

    // Fallback на mock данные если API недоступен
    const orgData = orgConfig ?? MOCK_ORGS[orgSlug] ?? MOCK_ORGS['www.tlh.ru']

    // Фильтруем модули по features организации
    const enabledFeatures = orgConfig?.features ?? []
    let modules: ModuleManifest[]
    let features: string[]

    if (enabledFeatures.length > 0) {
      // Используем реальные features из Admin API
      modules = MOCK_MODULES.filter((m) => enabledFeatures.includes(m.id))
      features = modules.flatMap((m) => m.features ?? [])
    } else {
      // Fallback: mock-логика для обратной совместимости
      if (orgSlug === 'university') {
        modules = MOCK_MODULES
      } else if (orgSlug === 'corporate') {
        modules = MOCK_MODULES.filter((m) => m.id !== 'lms')
      } else {
        modules = MOCK_MODULES.filter((m) => ['tagcloud', 'presentations'].includes(m.id))
      }
      features = modules.flatMap((m) => m.features ?? [])
    }

    // Языки организации из конфига или fallback
    const orgLanguages = orgConfig?.languages?.length
      ? orgConfig.languages
      : orgSlug === 'www.tlh.ru'
        ? PLATFORM_LANGUAGES.map((l) => l.code)
        : ['ru', 'en']

    // Название и описание — передаём полный LocalizedString для реактивной локализации
    const orgName =
      orgConfig?.name ?? (typeof orgData.name === 'string' ? { ru: orgData.name } : { ru: orgSlug })
    const orgDescription = orgConfig?.description

    // Для логотипа берём русское название как fallback
    const logoText = (typeof orgName === 'string' ? orgName : orgName.ru).slice(0, 3).toUpperCase()

    return {
      org: {
        id: orgConfig?.id ?? orgData.id ?? `org-${orgSlug}`,
        slug: orgSlug,
        name: orgName,
        type: (orgConfig?.type ?? orgData.type ?? 'custom') as OrgContext['type'],
        branding: {
          logo: orgConfig?.logo ?? `text:${logoText}`,
          appName: orgName,
          appNameShort: orgSlug,
          description: orgDescription,
        },
        colors: {
          primary: '#15803d',
          secondary: '#166534',
          accent: '#65a30d',
          background: '#0a0f0d',
          surface: '#1a2e1a',
          error: '#dc2626',
          warning: '#f59e0b',
          success: '#22c55e',
          info: '#3b82f6',
        },
        languages: orgLanguages,
      },
      modules,
      features,
      versions: {
        manifest: '1.2.0',
        modules: modules.reduce(
          (acc, m) => {
            acc[m.id] = m.version
            return acc
          },
          {} as Record<string, string>
        ),
        timestamp: new Date().toISOString(),
      },
    }
  }

  /**
   * Получить манифест конкретного модуля
   */
  async getModuleManifest(moduleId: string): Promise<ModuleManifest | null> {
    const cacheKey = CACHE_KEYS.module(moduleId)

    // TODO: Получать версию из API
    const version = '1.0.0'

    return this.cache.getWithVersion(cacheKey, version, async () => {
      return this.fetchModuleManifest(moduleId)
    })
  }

  /**
   * Получить манифест без кеширования
   */
  async fetchModuleManifest(moduleId: string): Promise<ModuleManifest | null> {
    // TODO: Реальный запрос к API
    // const response = await fetch(`${this.config.apiBase}/modules/${moduleId}/manifest`)
    // if (!response.ok) return null
    // return response.json()

    // Mock
    await this.simulateNetworkDelay()
    return MOCK_MODULES.find((m) => m.id === moduleId) ?? null
  }

  /**
   * Проверить доступность модуля для пользователя
   */
  checkModuleAccess(
    manifest: ModuleManifest,
    userRoles: string[] = [],
    isAuthenticated = false
  ): { allowed: boolean; reason?: string } {
    // Проверка авторизации
    if (manifest.requiresAuth && !isAuthenticated) {
      return { allowed: false, reason: 'auth_required' }
    }

    // Проверка ролей
    if (manifest.requiredRoles && manifest.requiredRoles.length > 0) {
      const hasRole = manifest.requiredRoles.some((role) => userRoles.includes(role))
      if (!hasRole) {
        return { allowed: false, reason: 'role_required' }
      }
    }

    return { allowed: true }
  }

  /**
   * Инвалидировать кеш модулей организации
   */
  invalidateOrgCache(orgSlug: string): void {
    this.cache.invalidate(CACHE_KEYS.orgModules(orgSlug))
    this.cache.invalidate(CACHE_KEYS.orgManifest(orgSlug))
  }

  /**
   * Инвалидировать кеш модуля
   */
  invalidateModuleCache(moduleId: string): void {
    this.cache.invalidate(CACHE_KEYS.module(moduleId))
  }

  /**
   * Симуляция сетевой задержки для mock данных
   */
  private simulateNetworkDelay(): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, 100 + Math.random() * 200))
  }
}

// ============================================================================
// Singleton instance
// ============================================================================

let registryInstance: RegistryService | null = null

/**
 * Получить singleton экземпляр RegistryService
 */
export function getRegistryService(config?: Partial<RegistryConfig>): RegistryService {
  if (!registryInstance) {
    registryInstance = new RegistryService(config)
  }
  return registryInstance
}

/**
 * Создать новый экземпляр RegistryService (для тестирования)
 */
export function createRegistryService(config?: Partial<RegistryConfig>): RegistryService {
  return new RegistryService(config)
}

export default RegistryService
